import React, { Fragment, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import styled, { createGlobalStyle } from 'styled-components';

import { colors } from 'app/variables';
import { useDebouncedCallback } from 'app/hooks';
import TextField from 'app/commonComponents/TextField';
import UnstyledButton from 'app/commonComponents/UnstyledButton';
import { CloseIcon } from 'app/commonComponents/icons';

import { useAppContext } from '../../hooks';

const AutosuggestStyles = createGlobalStyle`
  .react-autosuggest__container {
    position: relative;
  }
  .react-autosuggest__suggestions-container {
    background-color: ${colors.white};
    border: 1px solid ${colors.silver};
    border-radius: 0.4rem;
    cursor: pointer;
    left: 0;
    position: absolute;
    top: 100%;
    width: 100%;
    z-index: 2;
  }
  .react-autosuggest__suggestions-list {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }
  .react-autosuggest__suggestion {
    padding: 0.6rem 1.2rem;
  }
  .react-autosuggest__suggestion--highlighted {
    background-color: ${colors.whiteSand};
  }
`;

const StyledTextField = styled(TextField)`
  &:disabled {
    background-color: ${colors.white} !important;
  }
`;

function PlacesSearchField({
  name,
  label,
  value,
  error,
  onBlur,
  onChange,
  onSelect,
  onClear,
  getValue = (prediction) => prediction.description,
  searchType,
  countryCode,
  disabled,
}) {
  const { config, placesService } = useAppContext();

  const [touched, setTouched] = useState(false);
  const [predictions, setPredictions] = useState([]);

  const ignoreBlur = useRef();

  const showError = touched && !!error;

  const getPredictions = useDebouncedCallback(
    async ({ value }) => {
      try {
        if (value.length < 4) {
          clearPredictions();
          return;
        }
        let predictions = await placesService.getPredictions({
          input: value,
          types: [searchType],
          componentRestrictions: {
            country: countryCode,
          },
        });
        // if the search with a countryCode returned an empty array, try again without the country restriction
        if (predictions.length === 0 && countryCode) {
          predictions = await placesService.getPredictions({
            input: value,
            types: [searchType],
          });
        }
        setPredictions(predictions);
      } catch {
        clearPredictions();
      }
    },
    [countryCode, searchType],
    250
  );

  const selectPrediction = async (_, { suggestion }) => {
    ignoreBlur.current = true;
    onSelect(suggestion);
    setTouched(true);
  };

  const clearPredictions = () => {
    setPredictions([]);
  };

  const handleBlur = () => {
    if (ignoreBlur.current) {
      ignoreBlur.current = false;
      return;
    }
    onBlur?.();
    setTouched(true);
    clearPredictions();
  };

  const handleChange = (_, { newValue }) => {
    onChange(newValue);
    setTouched(false);
  };

  return (
    <Fragment>
      <Autosuggest
        suggestions={predictions}
        onSuggestionsFetchRequested={getPredictions}
        onSuggestionsClearRequested={clearPredictions}
        onSuggestionSelected={selectPrediction}
        getSuggestionValue={getValue}
        inputProps={{
          type: 'search',
          value,
          onBlur: handleBlur,
          onChange: handleChange,
          disabled,
        }}
        highlightFirstSuggestion
        focusInputOnSuggestionClick={false}
        renderSuggestion={(prediction) => prediction.description}
        renderInputComponent={(inputProps) => {
          const { key, ...rest } = inputProps;
          return (
            <StyledTextField
              key={key}
              name={name}
              label={label}
              error={showError && error}
              addon={
                onClear
                  ? {
                      label: (
                        <div
                          style={{
                            alignItems: 'center',
                            display: 'flex',
                            height: '100%',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            pointerEvents: 'all',
                          }}
                        >
                          <UnstyledButton onClick={onClear}>
                            <CloseIcon xsmall style={{ display: 'block' }} />
                          </UnstyledButton>
                        </div>
                      ),
                      width: '4rem',
                    }
                  : undefined
              }
              {...rest}
            />
          );
        }}
        renderSuggestionsContainer={({ containerProps, children }) => {
          if (!children) {
            return null;
          }

          const { key, ...rest } = containerProps;

          return (
            <div key={key} {...rest} data-testid="suggestions-container">
              <div>{children}</div>
              <div>
                <img
                  src={`${config.static_url}images/powered_by_google_on_white.png`}
                  style={{
                    display: 'block',
                    margin: '0.6rem auto 1.2rem',
                    width: '100px',
                  }}
                />
              </div>
            </div>
          );
        }}
      />
      <AutosuggestStyles />
    </Fragment>
  );
}

PlacesSearchField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  error: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  getValue: PropTypes.func,
  searchType: PropTypes.string.isRequired,
  countryCode: PropTypes.string,
  disabled: PropTypes.bool,
};

export default PlacesSearchField;
